import { supabase } from '@/integrations/supabase/client';

let signingOut = false;

/**
 * Force a hard sign-out: clear all Supabase auth tokens from localStorage,
 * clear app-level sessionStorage, call supabase.auth.signOut (local scope),
 * and hard-redirect to /login. Idempotent — multiple parallel calls collapse
 * into one redirect.
 *
 * Used when we detect a malformed/expired/invalid token that the SDK can't
 * recover from on its own. The window.location.replace ensures all React
 * state and React Query caches are wiped along with the page reload.
 */
export async function forceSignOut(
  reason: string = 'session_expired',
  extra?: { timeoutMinutes?: number; lastActivityAt?: number | null },
): Promise<void> {
  if (signingOut) return;
  signingOut = true;

  // Audit-log før vi rydder tokens (fire-and-forget, må ikke blokere logout)
  try {
    const { data: sess } = await supabase.auth.getSession();
    const userId = sess?.session?.user?.id ?? null;
    let companyId: string | null = null;
    try {
      companyId = sessionStorage.getItem('platform_active_company');
    } catch { /* ignore */ }
    await supabase.from('auth_logout_audit').insert({
      user_id: userId,
      company_id: companyId,
      reason,
      timeout_minutes: extra?.timeoutMinutes ?? null,
      last_activity_at: extra?.lastActivityAt ? new Date(extra.lastActivityAt).toISOString() : null,
      user_agent: typeof navigator !== 'undefined' ? navigator.userAgent.slice(0, 500) : null,
    });
  } catch {
    /* audit er best-effort */
  }

  try {
    // Defensive cleanup of all Supabase auth tokens (current + any stale)
    try {
      const keysToRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && (key.startsWith('sb-') || key.includes('-auth-token'))) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach((k) => localStorage.removeItem(k));
    } catch {
      /* localStorage may be unavailable in private mode */
    }

    try {
      sessionStorage.removeItem('app_mode');
      sessionStorage.removeItem('platform_active_company');
    } catch {
      /* ignore */
    }

    try {
      await supabase.auth.signOut({ scope: 'local' });
    } catch {
      /* token may already be invalid; ignore */
    }

    const path = window.location.pathname;
    const onPublicRoute =
      path === '/login' ||
      path === '/' ||
      path.startsWith('/portal') ||
      path.startsWith('/feedback') ||
      path.startsWith('/share') ||
      path.startsWith('/reset-password') ||
      path.startsWith('/landing');

    if (!onPublicRoute) {
      window.location.replace(`/login?reason=${encodeURIComponent(reason)}`);
    } else {
      // Already on a public route — just reset the flag so future logouts can fire
      signingOut = false;
    }
  } catch (err) {
    console.error('[forceSignOut] failed:', err);
    signingOut = false;
  }
}

/**
 * Returns true if the given error message indicates a fundamentally broken
 * auth token (malformed, wrong signing key, missing claims, etc.) — these
 * cannot be recovered by autoRefreshToken and require a hard re-login.
 */
export function isFatalAuthError(error: unknown): boolean {
  const err = error as any;
  const msg: string = typeof err?.message === 'string' ? err.message : '';
  const code: string = typeof err?.code === 'string' ? err.code : '';
  const status: number | null = err?.status ?? err?.context?.status ?? null;

  if (
    msg.includes('session_not_found') ||
    msg.includes('Session not found') ||
    msg.includes('Invalid Refresh Token') ||
    msg.includes('Refresh Token Not Found') ||
    msg.includes('JWT expired') ||
    msg.includes('Auth session missing') ||
    msg.includes('token is malformed') ||
    msg.includes('invalid number of segments') ||
    msg.includes('missing sub claim') ||
    msg.includes('invalid claim') ||
    msg.includes('token signature is invalid') ||
    msg.includes('bad_jwt') ||
    msg.includes('invalid JWT')
  ) {
    return true;
  }

  if (code === 'bad_jwt' || code === 'session_not_found') return true;
  if (status === 401) return true;

  return false;
}
